import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// Shadcn UI components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { FileInput } from '../components/ui/file-input';
import { FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { PlacaInput } from '../components/ui/placa-input';
import { CheckCircle } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { MANUFACTURERS } from '../utils/manufacturers';
import { VEHICLE_STATUS_OPTIONS } from '../utils/vehicleStatuses';

function VehicleFormWithPlaca({ id, defaults }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { control, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: defaults || {}
  });
  const [activeTab, setActiveTab] = useState("placa");
  const isEditing = Boolean(id);
  
  // Estado para controlar o diálogo de confirmação
  const [showPlacaDialog, setShowPlacaDialog] = useState(false);
  const [placaData, setPlacaData] = useState(null);
  
  // Ref para guardar o patioId inicial (evita reset quando lessors carrega)
  const initialPatioIdRef = useRef(null);
  
  /* -------- reset form when defaults change -------- */
  useEffect(() => {
    if (defaults) {
      console.log('🔄 Carregando defaults para edição:', defaults);
      console.log('   historico completo:', defaults.historico);
      console.log('   lessorId:', defaults.historico?.lessorId);
      console.log('   patioId:', defaults.historico?.patioId);
      
      // Normaliza os dados: se lessorId é objeto, extrai apenas o _id
      const normalizedDefaults = {
        ...defaults,
        historico: {
          ...defaults.historico,
          lessorId: defaults.historico?.lessorId?._id || defaults.historico?.lessorId,
          // patioId já é number, não precisa normalizar
        }
      };
      
      // Guardar patioId inicial
      if (normalizedDefaults.historico?.patioId !== undefined) {
        initialPatioIdRef.current = normalizedDefaults.historico.patioId;
        console.log('💾 Salvando patioId inicial:', initialPatioIdRef.current);
      }
      
      console.log('✅ Dados normalizados:', normalizedDefaults);
      console.log('   historico.lessorId:', normalizedDefaults.historico?.lessorId);
      console.log('   historico.patioId:', normalizedDefaults.historico?.patioId);
      
      reset(normalizedDefaults);
      // Se já temos dados, vamos direto para a aba de informações
      setActiveTab("info");
    }
  }, [defaults, reset]);

  /* -------- locadoras e pátios -------- */
  const [lessors, setLessors] = useState([]);
  useEffect(() => { 
    console.log('👤 Usuário atual:', user);
    console.log('   Role:', user?.role);
    console.log('   CompanyId:', user?.companyId);
    
    // Se for master, busca todas as locadoras
    // Se for locadora, usa apenas a empresa do usuário
    if (user?.role === 'master') {
      console.log('🔑 Master: buscando todas as locadoras');
      api.get('/locadoras').then(r => {
        console.log('✅ Locadoras recebidas:', r.data);
        setLessors(r.data);
      }).catch(err => {
        console.error('❌ Erro ao carregar locadoras:', err);
        setLessors([]);
      });
    } else if (user?.companyId) {
      console.log('🏢 Locadora: buscando apenas sua empresa');
      // Para usuários de locadora, buscar os dados completos da empresa
      api.get('/locadoras').then(r => {
        console.log('✅ Dados da locadora recebidos:', r.data);
        setLessors(r.data);
      }).catch(err => {
        console.error('❌ Erro ao carregar locadora:', err);
        setLessors([]);
      });
    }
  }, [user]);

  const selectedLessor = watch('historico.lessorId');
  const [patios, setPatios] = useState([]);
  useEffect(() => {
    console.log('🏭 Locadora selecionada mudou:', selectedLessor);
    console.log('   Locadoras disponíveis:', lessors.length);
    
    if (!selectedLessor) { 
      console.log('⚠️ Nenhuma locadora selecionada');
      setPatios([]); 
      return; 
    }
    
    // Extrair o ID caso selectedLessor seja um objeto (durante reset inicial)
    const lessorId = typeof selectedLessor === 'object' ? selectedLessor?._id : selectedLessor;
    console.log('   ID da locadora normalizado:', lessorId);
    
    // Se selectedLessor é objeto populado com pátios, usar diretamente
    let lessor;
    if (typeof selectedLessor === 'object' && selectedLessor?.patios) {
      lessor = selectedLessor;
      console.log('   Usando locadora do objeto populado:', lessor.nomeFantasia);
    } else {
      // Caso contrário, buscar no array de lessors
      lessor = lessors.find(l => l._id === lessorId);
      console.log('   Locadora encontrada no array:', lessor?.nomeFantasia);
    }
    
    console.log('   Pátios da locadora:', lessor?.patios?.length || 0);
    
    // Adiciona índice aos pátios para usar como identificador
    const patiosComId = (lessor?.patios || []).map((p, idx) => ({
      ...p,
      _id: idx, // Usa o índice como ID
      displayName: `${p.logradouro || 'Sem endereço'}, ${p.numero || 'S/N'} - ${p.cidade || ''}/${p.estado || ''}`
    }));
    
    console.log('✅ Pátios preparados:', patiosComId);
    setPatios(patiosComId);
    
    // Restaurar patioId inicial após os pátios carregarem (modo edição)
    console.log('🔍 Verificando restauração do patioId:');
    console.log('   initialPatioIdRef.current:', initialPatioIdRef.current);
    console.log('   patiosComId.length:', patiosComId.length);
    
    if (initialPatioIdRef.current !== null && patiosComId.length > 0) {
      const currentPatioId = watch('historico.patioId');
      console.log('   currentPatioId:', currentPatioId);
      console.log('   currentPatioId === undefined?', currentPatioId === undefined);
      console.log('   currentPatioId === 0?', currentPatioId === 0);
      
      if (currentPatioId === undefined) {
        console.log('🔄 Restaurando patioId inicial:', initialPatioIdRef.current);
        setValue('historico.patioId', initialPatioIdRef.current);
      } else {
        console.log('⏭️ PatioId já está definido, não precisa restaurar');
      }
    }
  }, [selectedLessor, lessors, setValue, watch]);

  /* -------- uploads -------- */
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [docs, setDocs] = useState([]);

  // Handlers para arquivos
  const handlePhotosChange = (files) => {
    setPhotos(prevPhotos => [...prevPhotos, ...files]);
  };
  
  const handleVideosChange = (files) => {
    setVideos(prevVideos => [...prevVideos, ...files]);
  };
  
  const handleDocsChange = (files) => {
    setDocs(prevDocs => [...prevDocs, ...files]);
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleRemoveVideo = (index) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleRemoveDoc = (index) => {
    setDocs(prev => prev.filter((_, i) => i !== index));
  };
  
  // Manipulador para quando os dados da placa forem encontrados
  const handlePlacaDataFound = (vehicleData) => {
    setPlacaData(vehicleData);
    setShowPlacaDialog(true);
  };
  
  // Preencher o formulário com os dados da placa
  const applyPlacaData = () => {
    // Preencher os campos do formulário com os dados obtidos
    Object.entries(placaData).forEach(([key, value]) => {
      if (typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (subValue !== null && subValue !== undefined) {
            setValue(`${key}.${subKey}`, subValue);
          }
        });
      } else if (value !== null && value !== undefined) {
        setValue(key, value);
      }
    });
    
    // Fechar o diálogo
    setShowPlacaDialog(false);
    
    // Avançar para a próxima aba
    setActiveTab('info');
  };

  /* -------- submit -------- */
  const onSubmit = async data => {
    try {
      // Pré-processamento dos dados para garantir tipos corretos
      if (data.infoTecnica) {
        // Converte campos numéricos para Number
        if (data.infoTecnica.anoFabricacao) data.infoTecnica.anoFabricacao = Number(data.infoTecnica.anoFabricacao);
        if (data.infoTecnica.anoModelo) data.infoTecnica.anoModelo = Number(data.infoTecnica.anoModelo);
        if (data.infoTecnica.km) data.infoTecnica.km = Number(data.infoTecnica.km);
      }
      
      if (data.comercial) {
        // Converte campos de preço para Number
        if (data.comercial.precoAquisicao) data.comercial.precoAquisicao = Number(data.comercial.precoAquisicao);
        if (data.comercial.precoReparo) data.comercial.precoReparo = Number(data.comercial.precoReparo);
        if (data.comercial.valorVenda) data.comercial.valorVenda = Number(data.comercial.valorVenda);
        if (data.comercial.valorFipe) data.comercial.valorFipe = Number(data.comercial.valorFipe);
      }

      // Converte patioId para Number (é o índice do array)
      if (data.historico?.patioId !== undefined && data.historico.patioId !== null && data.historico.patioId !== '') {
        console.log('🔄 Convertendo patioId:', data.historico.patioId, 'para Number');
        data.historico.patioId = Number(data.historico.patioId);
        console.log('✅ patioId convertido:', data.historico.patioId, typeof data.historico.patioId);
      } else {
        console.log('⚠️ patioId não definido:', data.historico?.patioId);
      }

      // cria FormData com anexos
      const fd = new FormData();
      
      // Adiciona os dados JSON
      fd.append('json', JSON.stringify(data));
      
      // Adiciona os arquivos somente se eles existirem
      if (photos && photos.length > 0) {
        photos.forEach(f => {
          if (f instanceof File) {
            fd.append('fotos', f);
          }
        });
      }
      
      if (videos && videos.length > 0) {
        videos.forEach(f => {
          if (f instanceof File) {
            fd.append('videos', f);
          }
        });
      }
      
      if (docs && docs.length > 0) {
        docs.forEach(f => {
          if (f instanceof File) {
            fd.append('anexos', f);
          }
        });
      }
      
      console.log('Enviando dados para o servidor:', data);
      console.log('Arquivos:', { fotos: photos.length, videos: videos.length, anexos: docs.length });

      // envia
      const req = !isEditing
        ? api.post('/veiculos', fd)
        : api.put(`/veiculos/${id}`, fd);
      
      const response = await req;
      console.log('Veículo salvo com sucesso:', response.data);
      navigate('/admin/veiculos');
    } catch (error) {
      console.error('Erro ao salvar veículo:', error);
      alert(t('vehicleForm.saveError', { 
        action: isEditing ? t('vehicleForm.update') : t('vehicleForm.register'), 
        error: error.response?.data?.error || error.message 
      }));
    }
  };

  const formFields = [
    { name: 'placa', label: t('vehicleForm.fields.placa'), size: 4, required: true, readOnly: Boolean(placaData) },
    { name: 'placaMercosul', label: t('vehicleForm.fields.placaMercosul'), size: 4 },
    { name: 'infoTecnica.marca', label: t('vehicleForm.fields.marca'), size: 4, type: 'select', options: MANUFACTURERS.map(m => ({ value: m, label: m })) },
    { name: 'infoTecnica.modelo', label: t('vehicleForm.fields.modelo'), size: 4 },
    { name: 'infoTecnica.versao', label: t('vehicleForm.fields.versao'), size: 4 },
    { name: 'infoTecnica.anoFabricacao', label: t('vehicleForm.fields.anoFabricacao'), size: 2 },
    { name: 'infoTecnica.anoModelo', label: t('vehicleForm.fields.anoModelo'), size: 2 },
    { name: 'infoTecnica.cor', label: t('vehicleForm.fields.cor'), size: 2 },
    { name: 'infoTecnica.combustivel', label: t('vehicleForm.fields.combustivel'), size: 3 },
    { name: 'infoTecnica.cambio', label: t('vehicleForm.fields.cambio'), size: 3 },
    { name: 'infoTecnica.km', label: t('vehicleForm.fields.km'), size: 3 },
    { name: 'infoTecnica.renavam', label: t('vehicleForm.fields.renavam'), size: 4 },
    { name: 'infoTecnica.chassi', label: t('vehicleForm.fields.chassi'), size: 8 }
  ];

  const commercialFields = [
    { name: 'historico.lessorId', label: t('vehicleForm.fields.locadora'), type: 'select', options: lessors, optionValue: '_id', optionLabel: 'nomeFantasia', size: 6 },
    { name: 'historico.patioId', label: t('vehicleForm.fields.patio'), type: 'select', options: patios, optionValue: '_id', optionLabel: 'displayName', size: 6 },
    { name: 'historico.status', label: t('vehicleForm.fields.status'), type: 'select', options: VEHICLE_STATUS_OPTIONS, size: 6 },
    { name: 'comercial.valorVenda', label: t('vehicleForm.fields.valorVenda'), type: 'currency', size: 4 },
    { name: 'comercial.precoAquisicao', label: t('vehicleForm.fields.precoAquisicao'), type: 'currency', size: 4 },
    { name: 'comercial.precoReparo', label: t('vehicleForm.fields.precoReparo'), type: 'currency', size: 4 },
    { name: 'comercial.valorFipe', label: t('vehicleForm.fields.valorFipe'), type: 'currency', size: 4 },
  ];

  /* ---------------- UI ---------------- */
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {!isEditing ? t('vehicleForm.title') : t('vehicleForm.editTitle')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {!isEditing 
            ? 'Preencha as informações do veículo para cadastrá-lo no sistema' 
            : 'Edite as informações do veículo cadastrado'}
        </p>
      </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Tabs 
              defaultValue="placa" 
              value={activeTab} 
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="mb-6">
                {!isEditing && <TabsTrigger value="placa">{t('vehicleForm.tabs.plateQuery')}</TabsTrigger>}
                <TabsTrigger value="info">{t('vehicleForm.tabs.info')}</TabsTrigger>
                <TabsTrigger value="commercial">{t('vehicleForm.tabs.commercial')}</TabsTrigger>
                <TabsTrigger value="media">{t('vehicleForm.tabs.media')}</TabsTrigger>
                <TabsTrigger value="docs">{t('vehicleForm.tabs.docs')}</TabsTrigger>
              </TabsList>

              {/* Tab: Consulta por Placa */}
              {!isEditing && (
                <TabsContent value="placa">
                  <Card className="p-6">
                    <div className="space-y-6">
                      <div className="max-w-2xl">
                        <h3 className="text-xl font-semibold mb-2">{t('vehicleForm.plateQuery.title')}</h3>
                        <p className="mb-6 text-muted-foreground">
                          {t('vehicleForm.plateQuery.description')}
                        </p>
                      
                        <Controller
                          name="placa"
                          control={control}
                          render={({ field }) => (
                            <PlacaInput
                              onChange={field.onChange}
                              onDataFound={handlePlacaDataFound}
                            />
                          )}
                        />
                      
                        <div className="mt-8">
                          <p className="text-sm text-muted-foreground">
                            {t('vehicleForm.plateQuery.afterQuery')}
                          </p>
                          <div className="flex justify-end mt-6">
                            <Button 
                              type="button"
                              onClick={() => setActiveTab('info')}
                            >
                              {t('vehicleForm.plateQuery.skipManual')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </TabsContent>
              )}

              {/* Tab: Informações */}
              <TabsContent value="info">
                <Card className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">Informações Técnicas</h3>
                    <p className="text-muted-foreground text-sm">Dados básicos e características do veículo</p>
                  </div>

                  <div className="grid grid-cols-12 gap-6">
                    {formFields.map((field) => (
                      <div key={field.name} className={`col-span-12 md:col-span-${field.size}`}>
                        <Controller
                          name={field.name}
                          control={control}
                          rules={{ required: field.required }}
                          render={({ field: inputField, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel htmlFor={field.name} className={`font-semibold ${field.required ? 'after:content-["*"] after:ml-1 after:text-red-500' : ''}`}>
                                {field.label}
                              </FormLabel>
                            
                              {field.type === 'select' ? (
                                <Select
                                  onValueChange={(value) => {
                                    // Converter de volta para número se for patioId
                                    const finalValue = field.name === 'historico.patioId' ? Number(value) : value;
                                    inputField.onChange(finalValue);
                                  }}
                                  value={inputField.value !== undefined && inputField.value !== null ? String(inputField.value) : ''}
                                >
                                  <SelectTrigger className="h-11">
                                    <SelectValue placeholder={t('vehicleForm.selectPlaceholder', { field: field.label })} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map(option => (
                                      <SelectItem 
                                        key={field.optionValue ? option[field.optionValue] : option.value} 
                                        value={String(field.optionValue ? option[field.optionValue] : option.value)}
                                      >
                                        {field.optionLabel ? option[field.optionLabel] : option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  id={field.name}
                                  {...inputField}
                                  className={`h-11 ${error ? 'border-red-500' : ''}`}
                                  readOnly={field.readOnly}
                                />
                              )}
                              {error && <FormMessage>{error.message || t('vehicleForm.requiredField')}</FormMessage>}
                            </FormItem>
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              {/* Tab: Histórico & Comercial */}
              <TabsContent value="commercial">
                <Card className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">Informações Comerciais</h3>
                    <p className="text-muted-foreground text-sm">Valores, status e histórico do veículo</p>
                  </div>

                  <div className="grid grid-cols-12 gap-6">
                    {commercialFields.map((field) => (
                      <div key={field.name} className={`col-span-12 md:col-span-${field.size}`}>
                        <Controller
                          name={field.name}
                          control={control}
                          render={({ field: inputField, fieldState: { error } }) => (
                            <FormItem>
                              <FormLabel htmlFor={field.name} className="font-semibold">
                                {field.label}
                              </FormLabel>
                            
                              {field.type === 'select' ? (
                                <Select
                                  onValueChange={(value) => {
                                    // Converter de volta para número se for patioId
                                    const finalValue = field.name === 'historico.patioId' ? Number(value) : value;
                                    inputField.onChange(finalValue);
                                  }}
                                  value={inputField.value !== undefined && inputField.value !== null ? String(inputField.value) : ''}
                                >
                                  <SelectTrigger className="h-11">
                                    <SelectValue placeholder={t('vehicleForm.selectPlaceholder', { field: field.label })} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map(option => (
                                      <SelectItem 
                                        key={field.optionValue ? option[field.optionValue] : option.value} 
                                        value={String(field.optionValue ? option[field.optionValue] : option.value)}
                                      >
                                        {field.optionLabel ? 
                                          (typeof field.optionLabel === 'function' ? 
                                            field.optionLabel(option) : 
                                            option[field.optionLabel]) : 
                                          option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field.type === 'currency' ? (
                                <Input
                                  id={field.name}
                                  {...inputField}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0,00"
                                  className={`h-11 ${error ? 'border-red-500' : ''}`}
                                />
                              ) : (
                                <Input
                                  id={field.name}
                                  {...inputField}
                                  className={`h-11 ${error ? 'border-red-500' : ''}`}
                                />
                              )}
                            
                              {error && <FormMessage>{error.message}</FormMessage>}
                            </FormItem>
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              {/* Tab: Multimídia */}
              <TabsContent value="media">
                <Card className="p-6">
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">{t('vehicleForm.media.photos')}</h3>
                      <FileInput
                        multiple
                        accept="image/*"
                        buttonText={t('vehicleForm.media.selectPhotos')}
                        onChange={handlePhotosChange}
                        onRemove={handleRemovePhoto}
                      />
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">{t('vehicleForm.media.videos')}</h3>
                      <FileInput
                        multiple
                        accept="video/*"
                        buttonText={t('vehicleForm.media.selectVideos')}
                        onChange={handleVideosChange}
                        onRemove={handleRemoveVideo}
                      />
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Tab: Documentos */}
              <TabsContent value="docs">
                <Card className="p-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">{t('vehicleForm.docs.title')}</h3>
                    <FileInput
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      buttonText={t('vehicleForm.docs.selectDocuments')}
                      onChange={handleDocsChange}
                      onRemove={handleRemoveDoc}
                    />
                  </div>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/veiculos')}
              >
                {t('vehicleForm.cancel')}
              </Button>
              <Button type="submit">
                {t('vehicleForm.submitButton', { action: isEditing ? t('vehicleForm.update') : t('vehicleForm.register') })}
              </Button>
            </div>
          </form>
      
      {/* Diálogo de confirmação para aplicar os dados da placa */}
      <Dialog open={showPlacaDialog} onOpenChange={setShowPlacaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" /> 
              {t('vehicleForm.plateQuery.found')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <h3 className="mb-2 font-medium">{t('vehicleForm.plateQuery.foundDesc')}</h3>
            
            <div className="bg-gray-100 p-4 rounded-md space-y-2">
              <p><strong>{t('vehicleForm.fields.plate')}:</strong> {placaData?.placa}</p>
              <p><strong>{t('vehicleForm.fields.brand')}:</strong> {placaData?.infoTecnica?.marca}</p>
              <p><strong>{t('vehicleForm.fields.model')}:</strong> {placaData?.infoTecnica?.modelo}</p>
              <p><strong>{t('vehicleForm.fields.year')}:</strong> {placaData?.infoTecnica?.anoFabricacao}/{placaData?.infoTecnica?.anoModelo}</p>
              <p><strong>{t('vehicleForm.fields.color')}:</strong> {placaData?.infoTecnica?.cor}</p>
              <p><strong>{t('vehicleForm.fields.fuel')}:</strong> {placaData?.infoTecnica?.combustivel}</p>
              {placaData?.comercial?.valorFipe && (
                <p><strong>Valor FIPE:</strong> {formatCurrency(placaData.comercial.valorFipe)}</p>
              )}
            </div>
            
            <p className="mt-4 text-sm text-gray-600">
              {t('vehicleForm.plateQuery.reviewData')}
            </p>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPlacaDialog(false)}
            >
              {t('vehicleForm.buttons.cancel')}
            </Button>
            <Button onClick={applyPlacaData}>
              {t('vehicleForm.plateQuery.useData')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VehicleFormWithPlaca;
